import React, { useEffect, useState } from 'react';
import { View, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useCurrentApp } from '../../context/CurrentApp';
import { polygonService } from '../../services/central-api/polygons';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';
import FilterCheckboxList from './FilterCheckboxList';
import FilterRadioList, { FilterListItem } from './FilterRadioList';

type Props = {
    polygonType: string;
    setPolygonType: React.Dispatch<React.SetStateAction<string>>;
    setPendingLoading?: React.Dispatch<React.SetStateAction<boolean>>;
    companyId?: number;
    requiresCompanyId?: boolean;
} & ({
    multiple: true;
    setMapPolygonsIds: React.Dispatch<React.SetStateAction<number[]>>;
    mapPolygonsIds: number[];
} | {
    multiple: false;
    setMapPolygonsId: React.Dispatch<React.SetStateAction<number>>;
    mapPolygonsId: number;
});

export default function RegionsList(props: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const { isParentApp } = useCurrentApp();

    const [typeItems, setTypeItems] = useState<FilterListItem<string>[]>([]);
    const [typeIsLoading, setTypeIsLoading] = useState(true);
    const [search, setSearch] = useState('');

    const [polygonItems, setPolygonItems] = useState<FilterListItem[]>([]);
    const [polygonsIsLoading, setPolygonsIsLoading] = useState(false);

    async function getPolygonsType() {
        try {
            setTypeIsLoading(true);
            const polygonsType = await polygonService.getPolygonsType(
                isParentApp && props.companyId && props.requiresCompanyId ? props.companyId : undefined
            );
            setTypeItems(polygonsType.map((polygon) => ({
                label: polygon.type,
                value: polygon.type
            })));
        } catch (err) {
            console.error(err);
        } finally {
            setTypeIsLoading(false);
        }
    }

    async function getPolygonsByType(polygonType: string) {
        try {
            props.setPendingLoading?.(true);
            setPolygonsIsLoading(true);
            const resp = await polygonService.getPolygonsByType(
                polygonType,
                isParentApp && props.companyId && props.requiresCompanyId ? props.companyId : undefined
            );
            setPolygonItems(resp.map((polygon) => ({ label: polygon.name, value: polygon.id })));
        } catch (err) {
            console.error(err);
        } finally {
            props.setPendingLoading?.(false);
            setPolygonsIsLoading(false);
        }
    }

    useEffect(() => {
        if (props.requiresCompanyId && isParentApp && !props.companyId) {
            setTypeIsLoading(false);
            return;
        }
        getPolygonsType();
    }, [isParentApp, props.companyId]);

    useEffect(() => {
        if ((props.requiresCompanyId && isParentApp && !props.companyId) || !props.polygonType) {
            setPolygonItems([]);
            return;
        }
        setPolygonItems([]);
        getPolygonsByType(props.polygonType);
    }, [props.polygonType, isParentApp, props.companyId]);

    const toggleType = (type: string) => {
        const next = props.polygonType === type ? '' : type;
        if (props.multiple) {
            props.setMapPolygonsIds([]);
        } else {
            props.setMapPolygonsId(0);
        }
        props.setPolygonType(next);
    };

    const filtered = typeItems.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <View style={styles.container}>
            {typeItems.length >= 7 && (
                <View style={styles.searchContainer}>
                    <FontAwesomeIcon icon={faMagnifyingGlass as IconDefinition} fontSize={16} color={theme.colors.neutral['300']} />
                    <TextInput
                        style={styles.searchInput}
                        placeholder={translate('typeToSearch')}
                        placeholderTextColor={theme.colors.neutral['300']}
                        value={search}
                        onChangeText={setSearch}
                    />
                </View>
            )}

            <View style={styles.countRow}>
                <MyAppText style={styles.countText}>
                    {translate('items')}: {filtered.length}
                </MyAppText>
                <TouchableOpacity onPress={() => {
                    props.setPolygonType('');
                    if (props.multiple) {
                        props.setMapPolygonsIds([]);
                    } else {
                        props.setMapPolygonsId(0);
                    }
                }} disabled={props.polygonType === ''}>
                    <MyAppText style={[styles.clearSelection, props.polygonType === '' && styles.clearSelectionDisabled]}>
                        {translate('clearSelection')}
                    </MyAppText>
                </TouchableOpacity>
            </View>

            {typeIsLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size='small' color={theme.colors.primary['500']} />
                </View>
            ) : (
                <ScrollView style={styles.list} contentContainerStyle={{ gap: 8 }} nestedScrollEnabled>
                    {filtered.map((type) => {
                        const isExpanded = props.polygonType === type.value;
                        return (
                            <View key={type.value}>
                                <TouchableOpacity style={styles.typeRow} onPress={() => toggleType(type.value)}>
                                    <View style={styles.typeLeft}>
                                        <View style={[styles.radio, isExpanded && styles.radioSelected]}>
                                            {isExpanded && <View style={styles.radioInner} />}
                                        </View>
                                        <MyAppText style={styles.typeLabel}>{type.label}</MyAppText>
                                    </View>
                                    <FontAwesomeIcon
                                        icon={(isExpanded ? faChevronUp : faChevronDown) as IconDefinition}
                                        fontSize={12}
                                        color={theme.colors.primary['500']}
                                    />
                                </TouchableOpacity>

                                {isExpanded && (
                                    <View style={styles.subRegionsContainer}>
                                        {props.multiple
                                            ? <FilterCheckboxList
                                                items={polygonItems}
                                                value={props.mapPolygonsIds}
                                                setValue={props.setMapPolygonsIds}
                                                isLoading={polygonsIsLoading}
                                            />
                                            : <FilterRadioList
                                                items={polygonItems}
                                                value={props.mapPolygonsId}
                                                setValue={props.setMapPolygonsId}
                                                isLoading={polygonsIsLoading}
                                                clearValue={0}
                                                hideClearValue={true}
                                            />
                                        }
                                    </View>
                                )}
                            </View>
                        );
                    })}
                </ScrollView>
            )}
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        gap: 12,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.neutral['400'],
        outlineStyle: 'none',
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    clearSelectionDisabled: {
        opacity: 0.4,
    },
    loadingContainer: {
        paddingVertical: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    list: {
        maxHeight: 320,
    },
    typeRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    typeLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioSelected: {
        borderColor: theme.colors.primary['500'],
    },
    radioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.primary['500'],
    },
    typeLabel: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    subRegionsContainer: {
        paddingLeft: 34,
        paddingTop: 12,
    },
}));
